import React from "react";
import PropTypes from "prop-types";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { roundToTwoDecimals } from "../../../../utils/formHelper";
import WorkAreaCalculationTable from "./WorkAreaCalculationTable";
import "./WorkAreaAccordion.scss";

const formatHours = value => Number(value ?? 0).toFixed(2);

const formatCurrency = value =>
  `$${Number(roundToTwoDecimals(Number(value ?? 0))).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const buildTierRows = tiers =>
  (tiers ?? []).map(tier => ({
    tier: tier.payType,
    description: `${formatHours(tier.hours)} hrs × ${formatCurrency(tier.rate)}`,
    amount: formatCurrency(tier.amount),
    variant: "detail"
  }));

const buildWageRows = wage => {
  if (!wage) return [];
  return [
    ...buildTierRows(wage.tiers),
    { tier: "", description: "Wage required", amount: formatCurrency(wage.wageRequired), variant: "bold" },
    {
      tier: "",
      description: "Gross Amount Earned (7A)",
      amount: formatCurrency(wage.grossAmountEarned),
      variant: "muted"
    },
    { tier: "", description: "Wage back payment", amount: formatCurrency(wage.wageBackPayment), variant: "highlight" }
  ];
};

const buildFringeRows = fringe => {
  if (!fringe) return [];
  return [
    ...buildTierRows(fringe.tiers),
    { tier: "", description: "Fringe required", amount: formatCurrency(fringe.fringeRequired), variant: "bold" },
    {
      tier: "",
      description: "Total Fringe Benefit Credit (6B)",
      amount: formatCurrency(fringe.totalFringeBenefitCredit),
      variant: "muted"
    },
    {
      tier: "",
      description: "Payment in Lieu of Fringe Benefits (6C)",
      amount: formatCurrency(fringe.paymentInLieuOfFringeBenefits),
      variant: "muted"
    },
    {
      tier: "",
      description: "Fringe reported on payroll",
      amount: formatCurrency(fringe.fringeReportedOnPayroll),
      variant: "bold"
    },
    {
      tier: "",
      description: "Fringe back payment",
      amount: formatCurrency(fringe.fringeBackPayment),
      variant: "highlight"
    }
  ];
};

export default function WorkAreaAccordion({ index, workArea, expanded, onToggle }) {
  const { workAreaName, laborClassification, st, ot, dt, backPayment } = workArea;

  const nameLabel = workAreaName ? "Work Area Name" : laborClassification ? "Labor Classification" : "Work Area";
  const displayName = workAreaName || laborClassification || "";

  return (
    <Accordion className="workAreaAccordion" expanded={expanded} onChange={onToggle} disableGutters>
      <AccordionSummary className="workAreaAccordion-summary">
        <div className="workAreaAccordion-name">
          <span className="workAreaAccordion-badge">{index}</span>
          <div className="workAreaAccordion-nameText">
            <span className="workAreaAccordion-label">{nameLabel}</span>
            <span className="workAreaAccordion-value">{displayName}</span>
          </div>
        </div>

        <div className="workAreaAccordion-hours">
          <div className="workAreaAccordion-hoursItem">
            <span className="workAreaAccordion-label">ST</span>
            <span className="workAreaAccordion-value">{formatHours(st)}</span>
          </div>
          <div className="workAreaAccordion-hoursItem">
            <span className="workAreaAccordion-label">OT</span>
            <span className="workAreaAccordion-value">{formatHours(ot)}</span>
          </div>
          <div className="workAreaAccordion-hoursItem">
            <span className="workAreaAccordion-label">DT</span>
            <span className="workAreaAccordion-value">{formatHours(dt)}</span>
          </div>
        </div>

        <div className="workAreaAccordion-divider" />

        <div className="workAreaAccordion-backPayment">
          <span className="workAreaAccordion-label">
            Back Payment
            <Tooltip title="For more information, please contact your CSM or your tax advisor for additional support.">
              <IconButton
                size="small"
                className="workAreaAccordion-infoIcon"
                onClick={event => event.stopPropagation()}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </span>
          <span className="workAreaAccordion-value workAreaAccordion-value--alert">
            {formatCurrency(backPayment)}
          </span>
        </div>

        <div className="workAreaAccordion-toggle">
          <span className="workAreaAccordion-toggleLabel">
            {expanded ? "Hide calculation" : "Show calculation"}
          </span>
          <ExpandMoreIcon className="workAreaAccordion-expandIcon" />
        </div>
      </AccordionSummary>

      <AccordionDetails className="workAreaAccordion-details">
        {workArea.calculation ? (
          <div className="workAreaAccordion-calculationGrid">
            <WorkAreaCalculationTable title="Prevailing Wage" rows={buildWageRows(workArea.calculation.wage)} />
            <WorkAreaCalculationTable title="Prevailing Fringe" rows={buildFringeRows(workArea.calculation.fringe)} />
          </div>
        ) : (
          <Typography className="workAreaAccordion-noData">No Data</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

WorkAreaAccordion.propTypes = {
  index: PropTypes.number.isRequired,
  workArea: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workAreaName: PropTypes.string,
    laborClassification: PropTypes.string,
    st: PropTypes.number,
    ot: PropTypes.number,
    dt: PropTypes.number,
    backPayment: PropTypes.number
  }).isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
};

WorkAreaAccordion.defaultProps = {
  expanded: false
};